import clamp from "lodash/clamp";
import isEqual from "lodash/isEqual";
// eslint-disable-next-line boundaries/element-types
import { rotateArray, modulo } from "../../helpers/arrays";
import { vec2 } from "./vec2";
/** General-purpose 2d geometry helpers */
export class geom2 {
    /**
     * Returns the area of a triangle defined by three points.
     */
    static triangleArea(p1, p2, p3) {
        return Math.abs((p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y)) / 2);
    }
    /**
     * Returns clone of input polygon, with loopback edges removed.
     *
     * A loopback edge is an edge pair that occurs when the start of edge0 matches the end of edge1:
     *
     * edge0 = [v0 -> v1]
     * edge1 = [v1 -> v2]
     *
     * loopback edge = isEqual(v0, v2)
     *
     * The loopback edge is a degenerate case in that, it represents a portion of the geometries surface with zero area
     *
     * The implementation here is simple;
     * - assume polygons are defined as sequence of points,
     * - assume loopback edges only exist between vertex triples
     *
     * TODO:
     * account for more complex loopback cases arising between three of more edges in sequence with colocated vertices
     */
    static polygonRemoveLoopbackEdges(polygon, options) {
        const filteredPolygon = [];
        const epsilon = options?.epsilon ?? Number.EPSILON;
        for (let i = 0; i < polygon.length;) {
            const point = modulo(polygon, i + 0);
            if (!point) {
                i++;
                continue;
            }
            const point1 = modulo(polygon, i + 1);
            if (!point1) {
                i++;
                continue;
            }
            const point2 = modulo(polygon, i + 2);
            if (!point2) {
                i++;
                continue;
            }
            if (vec2.distance(point, point2) <= epsilon) {
                // Skip adding this point, jump ahead to the next equivalent point. potentially add that instead (exception being, if the next point is also part of a subsequent spike)
                i += 2;
                // TODO: consider detection of spikes that cover more than 2 points
                continue;
            }
            filteredPolygon.push(point);
            i++;
        }
        return filteredPolygon;
    }
    /**
     * Returns clone of input polygon, with collocated and sequentially adjacent vertices removed.
     *
     * Colocated vertices are;
     * - vertices that are within a given epsilon distance of each other,
     * - vertices that are adjacent in the polygons sequence of vertices
     *
     * consider:
     *
     * polygon = [v0, v1, v2, v3, v4]
     *
     * if distance(v0, v1) <= epsilon, then v1 is removed. v1 is removed because it is adjacent to v0 in the polygon sequence
     * if distance(v1, v3) <= epsilon, then v3 is not removed. v3 is not removed because it is not adjacent to v1 in the polygon sequence
     */
    static polygonRemoveColocatedVertices(polygon, options) {
        const filteredPolygon = [];
        const epsilon = options?.epsilon ?? Number.EPSILON;
        const pointFirst = modulo(polygon, 0);
        const pointLast = modulo(polygon, -1);
        // Skip the first point if it is colocated with the last point - simplifies the loop
        const iStart = pointFirst && pointLast && vec2.distance(pointFirst, pointLast) <= epsilon ? 1 : 0;
        let pointPrev = undefined;
        for (let i = iStart; i < polygon.length;) {
            const point = modulo(polygon, i + 0);
            if (!point) {
                i++;
                continue;
            }
            if (pointPrev) {
                if (vec2.distance(point, pointPrev) <= epsilon) {
                    i++;
                    continue;
                }
            }
            filteredPolygon.push(point);
            pointPrev = point;
            i++;
        }
        return filteredPolygon;
    }
    /**
     * Returns the area of a polygon
     */
    static polygonArea(polygon) {
        let area = 0;
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            const point1 = modulo(polygon, i);
            if (!point1) {
                continue;
            }
            const point2 = modulo(polygon, i + 1); // wrap around to the first vertex
            if (!point2) {
                continue;
            }
            area += point1.x * point2.y - point2.x * point1.y;
        }
        return Math.abs(area) / 2;
    }
    // https://stackoverflow.com/questions/6989100/sort-points-in-clockwise-order
    static clockwiseCompare(a, b, center) {
        if (a.x - center.x >= 0 && b.x - center.x < 0)
            return true;
        if (a.x - center.x < 0 && b.x - center.x >= 0)
            return false;
        if (a.x - center.x === 0 && b.x - center.x === 0) {
            if (a.y - center.y >= 0 || b.y - center.y >= 0)
                return a.y > b.y;
            return b.y > a.y;
        }
        // compute the cross product of vectors (center -> a) x (center -> b)
        const det = (a.x - center.x) * (b.y - center.y) - (b.x - center.x) * (a.y - center.y);
        if (det < 0)
            return true;
        if (det > 0)
            return false;
        // points a and b are on the same line from the center
        // check which point is closer to the center
        const d1 = (a.x - center.x) * (a.x - center.x) + (a.y - center.y) * (a.y - center.y);
        const d2 = (b.x - center.x) * (b.x - center.x) + (b.y - center.y) * (b.y - center.y);
        return d1 > d2;
    }
    static sortPolyClockwise(points) {
        let centerX = 0;
        let centerY = 0;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        points.forEach((p) => {
            centerX += p.x;
            centerY += p.y;
        });
        centerX /= points.length;
        centerY /= points.length;
        return points.sort((a, b) => (this.clockwiseCompare(a, b, { x: centerX, y: centerY }) ? 1 : -1));
    }
    /** Quite inefficient, useful for testing */
    static isPolygonSorted(points) {
        const sorted = this.sortPolyClockwise([...points]);
        for (let i = 0; i < sorted.length; i++) {
            if (isEqual(points, rotateArray(sorted, i))) {
                return true;
            }
        }
        return false;
    }
    /**
     * Distance between a point and a polygon, also finds the closest point on the polygon
     * https://iquilezles.org/articles/distfunctions2d/
     * https://stackoverflow.com/a/9557244
     * Should work for non-convex as well
     */
    static polygonPointDistance(p, v) {
        let minDist = vec2.chain(p).sub(v[0]).dot(vec2.sub(p, v[0]));
        let outPoint = v[0];
        let sign = 1.0;
        for (let i = 0, j = v.length - 1; i < v.length; j = i, i++) {
            // distance
            const AB = vec2.sub(v[j], v[i]);
            const AP = vec2.sub(p, v[i]);
            const magAB = vec2.dot(AB, AB);
            const ABAPproduct = vec2.dot(AP, AB);
            const distance = ABAPproduct / magAB;
            const a = vec2.scale(AB, clamp(distance, 0.0, 1.0));
            const b = vec2.sub(AP, a);
            const newDist = vec2.dot(b, b);
            const newPoint = vec2.add(v[i], a);
            if (newDist < minDist) {
                minDist = newDist;
                outPoint = newPoint;
            }
            // winding number from http://geomalgorithms.com/a03-_inclusion.htm
            const isInsidePoly = (p.y >= v[i].y && p.y < v[j].y && AB.x * AP.y > AB.y * AP.x) ||
                (!(p.y >= v[i].y) && !(p.y < v[j].y) && !(AB.x * AP.y > AB.y * AP.x));
            if (isInsidePoly) {
                sign *= -1.0;
            }
        }
        return { dist: sign * Math.sqrt(minDist), outPoint };
    }
    // NOTE: This expects a sorted convex polygon, the tests ensure that for the results of approxFittingPolygon
    // NOTE: returns null for empty input polygon
    static convexPolygonDistance(bcA, bcB) {
        // TODO: this could be optimized with binary search
        let minDist = Infinity;
        const allDists = [];
        const allPointsA = [];
        const allPointsB = [];
        // We need to evaluate it twice
        // 1. Points of polygon A vs segments of polygon B
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        bcA.forEach((pa) => {
            const { dist, outPoint } = this.polygonPointDistance(pa, bcB);
            allDists.push(dist);
            allPointsA.push(pa);
            allPointsB.push(outPoint);
            if (dist < minDist) {
                minDist = dist;
            }
        });
        // 2. Points of polygon B vs segments of polygon A
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        bcB.forEach((pb) => {
            const { dist, outPoint } = this.polygonPointDistance(pb, bcA);
            allDists.push(dist);
            allPointsA.push(outPoint);
            allPointsB.push(pb);
            if (dist < minDist) {
                minDist = dist;
            }
        });
        const minimalPointsA = allPointsA.filter((_p, i) => Math.abs(allDists[i] - minDist) < 0.0000001);
        const minimalPointsB = allPointsB.filter((_p, i) => Math.abs(allDists[i] - minDist) < 0.0000001);
        // NOTE: preventing the runtime error from an empty polygon input
        if (minimalPointsA.length === 0 || minimalPointsB.length === 0)
            return null;
        let minAX = minimalPointsA[0].x;
        let minAY = minimalPointsA[0].y;
        let maxAX = minimalPointsA[0].x;
        let maxAY = minimalPointsA[0].y;
        let minBX = minimalPointsB[0].x;
        let minBY = minimalPointsB[0].y;
        let maxBX = minimalPointsB[0].x;
        let maxBY = minimalPointsB[0].y;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        minimalPointsA.forEach((p) => {
            minAX = Math.min(p.x, minAX);
            minAY = Math.min(p.y, minAY);
            maxAX = Math.max(p.x, maxAX);
            maxAY = Math.max(p.y, maxAY);
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        minimalPointsB.forEach((p) => {
            minBX = Math.min(p.x, minBX);
            minBY = Math.min(p.y, minBY);
            maxBX = Math.max(p.x, maxBX);
            maxBY = Math.max(p.y, maxBY);
        });
        const minA = { x: minAX, y: minAY };
        const minB = { x: minBX, y: minBY };
        const maxA = { x: maxAX, y: maxAY };
        const maxB = { x: maxBX, y: maxBY };
        const pointA = vec2.chain(minA).add(maxA).scale(0.5).xy();
        const pointB = vec2.chain(minB).add(maxB).scale(0.5).xy();
        return {
            minDist,
            pointA,
            pointB,
            minA,
            minB,
            maxA,
            maxB,
        };
    }
    static isPolygonDiff(newPolygon, oldPolygon) {
        return (newPolygon.length !== oldPolygon.length ||
            newPolygon.some((xy, index) => xy.x !== oldPolygon[index]?.x || xy.y !== oldPolygon[index]?.y));
    }
    static arePolygonsDiff(newPolygons, oldPolygons) {
        return (newPolygons.length !== oldPolygons.length ||
            newPolygons.some((newPolygon, index) => {
                const oldPolygon = oldPolygons[index];
                if (!oldPolygon) {
                    return true;
                }
                else {
                    return this.isPolygonDiff(newPolygon, oldPolygon);
                }
            }));
    }
    /**
     * Determines if a polygon from ordered sequence of points, is convex or not.
     * Based on http://www.sunshine2k.de/coding/java/Polygon/Convex/polygon.htm
     */
    static isConvexFromPoints(points) {
        if (points.length < 3) {
            return false;
        }
        let result = 0;
        for (let i = 0; i < points.length; i++) {
            const pt0 = modulo(points, i);
            const pt1 = modulo(points, i + 1);
            const pt2 = modulo(points, i + 2);
            if (!pt0 || !pt1 || !pt2) {
                // If any of these pts are undefined, that'd be equivalent to the length < 3 case
                // which returns false
                return false;
            }
            const vec = vec2.sub(pt1, pt0);
            if (i === 0)
                // in first loop direction is unknown, so save it in res
                result = pt2.x * vec.y - pt2.y * vec.x + vec.x * pt0.y - vec.y * pt0.x;
            else {
                const newResult = pt2.x * vec.y - pt2.y * vec.x + vec.x * pt0.y - vec.y * pt0.x;
                if ((newResult > 0 && result < 0) || (newResult < 0 && result > 0)) {
                    return false;
                }
            }
        }
        return true;
    }
}
//# sourceMappingURL=geom2.js.map